import React, { useState } from "react";
import {
  Star,
  ChevronDown,
  ChevronUp,
  Calendar,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { formatDisplayDate } from "../utils/dateUtils";
import { renderRichText } from "../utils/richText";

const ImportantTasks = ({
  tabs = [],
  subGoals = [],
  onToggleGoal,
  onToggleSubGoal,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Collect all incomplete important goals from all tabs
  const importantGoals = [];

  (tabs || []).forEach((tab) => {
    if (tab && tab.goals && Array.isArray(tab.goals)) {
      tab.goals.forEach((goal) => {
        if (goal.important && !goal.completed) {
          importantGoals.push({
            ...goal,
            tabName: tab.name,
            tabId: tab.id,
            isSubGoal: false,
          });
        }
      });
    }
  });

  // Collect all incomplete important sub-goals
  (subGoals || []).forEach((subGoal) => {
    if (subGoal.important && !subGoal.completed) {
      // Find the parent goal and tab
      let parentGoal = null;
      let parentTab = null;

      (tabs || []).forEach((tab) => {
        if (tab && tab.goals && Array.isArray(tab.goals)) {
          const goal = tab.goals.find((g) => g.id === subGoal.goalId);
          if (goal) {
            parentGoal = goal;
            parentTab = tab;
          }
        }
      });

      if (parentGoal && parentTab) {
        importantGoals.push({
          ...subGoal,
          tabName: parentTab.name,
          tabId: parentTab.id,
          parentGoalText: parentGoal.text,
          isSubGoal: true,
        });
      }
    }
  });

  // Sort: goals with deadlines first (earliest to latest), then goals without deadlines
  importantGoals.sort((a, b) => {
    // If both have deadlines, sort by deadline
    if (a.deadline && b.deadline) {
      return new Date(a.deadline) - new Date(b.deadline);
    }
    // If only a has deadline, a comes first
    if (a.deadline && !b.deadline) return -1;
    // If only b has deadline, b comes first
    if (!a.deadline && b.deadline) return 1;
    // If neither has deadline, maintain original order
    return 0;
  });

  // If no important goals or sub-goals, don't show the component
  if (importantGoals.length === 0) {
    return null;
  }

  const renderGoalItem = (item) => (
    <div
      key={`${item.isSubGoal ? "sub" : "goal"}-${item.id}`}
      className={`flex items-center gap-2 bg-gray-700/50 p-2 rounded-lg group ${item.isSubGoal ? "ml-6" : ""}`}
    >
      {/* Completion Checkbox */}
      <button
        onClick={() => {
          if (item.isSubGoal) {
            onToggleSubGoal && onToggleSubGoal(item.id);
          } else {
            onToggleGoal && onToggleGoal(item.tabId, item.id);
          }
        }}
        className="flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity"
        title="Mark as completed"
      >
        {item.completed ? (
          <CheckCircle2
            className={`w-4 h-4 ${item.isSubGoal ? "text-green-400" : "text-yellow-400"}`}
          />
        ) : (
          <Circle
            className={`w-4 h-4 ${item.isSubGoal ? "text-cyan-300" : "text-cyan-400"}`}
          />
        )}
      </button>
      <Star className="w-4 h-4 text-yellow-400 flex-shrink-0 fill-yellow-400" />
      <div className="flex-1 min-w-0">
        <span
          className={`text-cyan-200 ${item.isSubGoal ? "text-xs" : "text-sm"} block truncate`}
        >
          {renderRichText(
            item.text,
            `imp-${item.isSubGoal ? "sg" : "g"}-${item.id}`,
            { collaborators: true },
          )}
        </span>
        {item.isSubGoal && item.parentGoalText && (
          <span className="text-gray-500 text-xs block truncate">
            ↳ {renderRichText(item.parentGoalText, `imp-parent-${item.id}`, { collaborators: true })}
          </span>
        )}
      </div>
      {item.deadline ? (
        <span className="text-gray-400 text-xs flex items-center gap-1 flex-shrink-0">
          <Calendar className="w-3 h-3" />
          {formatDisplayDate(new Date(item.deadline))}
        </span>
      ) : (
        <span className="text-gray-500 text-xs flex-shrink-0">No due date</span>
      )}
      <span className="text-gray-500 text-xs flex-shrink-0">
        ({item.tabName})
      </span>
    </div>
  );

  return (
    <div className="bg-gray-800 rounded-3xl p-6 border-2 border-yellow-500/30 shadow-lg mb-6">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex items-center justify-between w-full mb-4"
      >
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
          <h3 className="text-xl font-bold text-yellow-400">Important Tasks</h3>
          <span className="text-yellow-300 text-sm font-semibold">
            ({importantGoals.length})
          </span>
        </div>
        {isCollapsed ? (
          <ChevronDown className="w-5 h-5 text-yellow-400" />
        ) : (
          <ChevronUp className="w-5 h-5 text-yellow-400" />
        )}
      </button>

      {!isCollapsed && (
        <div className="space-y-2">{importantGoals.map(renderGoalItem)}</div>
      )}
    </div>
  );
};

export default ImportantTasks;
